/**
 * Typed contract for the SW <-> offscreen-document message bus.
 *
 * Phase 1 stood up the engine lifecycle (PING / INIT_ENGINE / STOP_ENGINE / STATUS).
 * Phase 2 extends this with the wallet, balance, merkleRoot, POI, and event-push
 * RPCs the SW needs to drop its in-process engine entirely.
 *
 * Wire format rules:
 *  - JSON-only payloads. Maps from the SDK (RailgunBalancesEvent, RailgunTokenBalanceMap)
 *    travel as `[key, value][]` arrays; the receiver rehydrates them.
 *  - Long-running calls (WALLET_GET_BALANCE) ack immediately and push the real result
 *    back via `OFFSCREEN_BALANCE_RESULT` with a caller-supplied `requestId`. This works
 *    around chrome.runtime.sendMessage's response-channel close that fires before the
 *    SDK can finish a multi-chain scan.
 *  - Engine events (balanceUpdate, batchListUpdate, scan progress) push from offscreen
 *    to SW via `OFFSCREEN_EVENT`. The SW rehydrates and re-emits via `railgunEmitter`
 *    so the existing controllers/UI subscribers don't change.
 */

export const RAILGUN_OFFSCREEN_TARGET = 'railgun-offscreen' as const
export const RAILGUN_OFFSCREEN_PORT_NAME = 'railgun-offscreen' as const

export type RailgunOffscreenInitParams = {
  merkletreeDbName: string
  artifactDbName: string
}

export type SerializedTokenBalance = {
  tokenAddress: string
  amount: string
  balanceBucket?: string
}

export type SerializedTokenBalanceMap = Array<[number, SerializedTokenBalance[]]>

export type SerializedBalanceResult = {
  balance: SerializedTokenBalanceMap
  pendingBalance: SerializedTokenBalanceMap
  missingInternalPOIBalance: SerializedTokenBalanceMap
  missingExternalPOIBalance: SerializedTokenBalanceMap
}

export type SerializedWalletInfo = {
  walletId: string
  railgunAddress: string
  encryptionKey: string
  viewingKey: string
}

/**
 * Phase 3.1 — per-chain session metadata. Sessions are a thin layer over the
 * single SDK engine; today they track lifecycle + lastSyncedBlock + timestamps,
 * persisted in chrome.storage.local. Phase 3.2 fills in real activate/hibernate
 * semantics (provider load/unload + incremental sync).
 */
export type SerializedChainSession = {
  chainId: number
  networkName: string
  status: 'cold' | 'activating' | 'active' | 'hibernating' | 'hibernated' | 'error'
  lastSyncedBlock: number | null
  lastActivatedAt: number | null
  lastHibernatedAt: number | null
  // Phase 3.2 — last on-chain merkleRoot anchor result.
  // valid=true: cache trustworthy. valid=false: poisoned, status flips to 'error'.
  // valid=null: could not check (no rpc, no merkletree, etc).
  merkleRootValid: boolean | null
  merkleRootCheckedAt: number | null
  merkleRootError?: string
  errorMessage?: string
}

export type SerializedMerkleRootResult = {
  networkName: string
  chainId: number
  treeNumber: number | null
  localRoot: string | null
  valid: true | false | null
  // Phase 4.1 — quorum confidence ('high' = 2+ RPCs agreed, 'low' = single RPC,
  // 'none' = no RPC succeeded). Optional for backwards compat with older results
  // persisted on session snapshots before 4.1.
  confidence?: 'high' | 'low' | 'none'
  rpcCount?: number
  error?: string
}

// Phase 4.2 — on-chain nullifier verification result. Caller passes a list
// of candidate nullifiers; `presence[n]` is true if that nullifier was found
// on-chain in the scanned block window. `quorumDisagreement` set when the
// fallback RPC saw nullifiers the primary missed (or vice versa) — caller
// MUST refuse the spend in that case.
export type SerializedNullifierStatusResult = {
  chainId: number
  presence: Record<string, boolean>
  fromBlock: number | null
  toBlock: number | null
  confidence: 'high' | 'low' | 'none'
  rpcCount: number
  quorumDisagreement?: boolean
  error?: string
}

export type RailgunOffscreenRequest =
  | { type: 'PING' }
  | { type: 'INIT_ENGINE'; params: RailgunOffscreenInitParams }
  | { type: 'STOP_ENGINE' }
  | { type: 'STATUS' }
  | { type: 'WALLET_CREATE_FROM_SEED'; mnemonic: string; accountIndex: number; passphrase?: string }
  | { type: 'WALLET_LOAD_BY_ID'; walletId: string; encryptionKey: string }
  | {
      type: 'WALLET_GET_BALANCE'
      walletId: string
      networkNames: string[]
      requestId: string
    }
  | {
      type: 'WALLET_FETCH_TOKEN_BALANCE'
      walletId: string
      networkName: string
      chainId: number
      tokenAddress: string
    }
  | { type: 'VERIFY_MERKLE_ROOTS'; networkNames: string[] }
  | {
      // Phase 4.2 — pre-spend nullifier check. Pass the nullifiers (lowercase
      // 0x-prefixed bytes32 hex) of the notes about to be spent + the chainId.
      // Optional fromBlock/toBlock narrow the scan range when caller knows the
      // last fully-synced block.
      type: 'VERIFY_NULLIFIERS'
      chainId: number
      nullifiers: string[]
      fromBlock?: number
      toBlock?: number
      lookbackBlocks?: number
    }
  | { type: 'REFRESH_RECEIVE_POIS'; walletId: string; chainId: number }
  | {
      // Sender-side POI submission kick. Generates + submits the wallet's pending
      // transact-proofs (spent-commitment POIs) to the aggregator so a *recipient's*
      // MissingExternalPOI clears. Mirrors REFRESH_RECEIVE_POIS but for the spend side.
      // Resolves as soon as the SDK's (floating) generate cycle is kicked — use
      // GET_PENDING_SPENT_POIS to poll for completion.
      type: 'GENERATE_SPENT_POIS'
      walletId: string
      chainId: number
    }
  | {
      // Completion check for GENERATE_SPENT_POIS: returns the on-chain txids whose
      // spent-commitment POIs are still un-submitted on this chain. Empty = done.
      type: 'GET_PENDING_SPENT_POIS'
      walletId: string
      chainId: number
    }
  | {
      // P1 POC (railgun-spend-engine-warm-plan.md §6) — multi-writer shared-DB
      // gate. Only honored when RAILGUN_SHARED_SCAN_DB is on. Runs the loaded
      // wallet's view-only scan across all worker chains into the shared DB, then
      // reads balances back on the main engine and compares. `walletId` is the
      // loaded (full) wallet whose viewing key drives the view-only scan.
      type: 'RUN_SHARED_DB_POC'
      walletId: string
    }
  | { type: 'SESSION_LIST' }
  | { type: 'SESSION_ACTIVATE'; chainId: number }
  | { type: 'SESSION_HIBERNATE'; chainId: number }
  | {
      // Phase 3.3 — opportunistic re-anchor sweep. Fired by a chrome.alarms
      // tick in the SW (~10 min). Re-runs `verifyMerkleRootsForNetworks` for
      // every registered chain regardless of active/hibernated status so the
      // UI's merkleRoot freshness indicator stays current without the user
      // having to switch chains. Best-effort: failures are logged on the
      // session, never thrown.
      type: 'SESSION_REANCHOR_ALL'
    }
  | {
      // Generic dispatch for any registered SDK method. Args/return are
      // bigint-safe-encoded via `encodeBigInts` / `decodeBigInts`. This is
      // how Phase 2.3 + 2.4 proxy the proving + populate + estimate calls
      // from UI to offscreen without typing each one individually.
      type: 'SDK_INVOKE'
      method: string
      argsEncoded: unknown
      // Optional id used to correlate async progress events back to a
      // specific invocation (proving runs send progress callbacks).
      progressId?: string
    }

export type RailgunOffscreenResponse =
  | { ok: true; type: 'PONG' }
  | { ok: true; type: 'ENGINE_READY' }
  | { ok: true; type: 'ENGINE_STOPPED' }
  | { ok: true; type: 'STATUS'; engineInitialized: boolean; bootedAt: number | null }
  | { ok: true; type: 'WALLET_INFO'; wallet: SerializedWalletInfo }
  | { ok: true; type: 'BALANCE_PENDING' }
  | { ok: true; type: 'TOKEN_BALANCE'; amount: string }
  | { ok: true; type: 'MERKLE_ROOTS'; results: SerializedMerkleRootResult[] }
  | { ok: true; type: 'NULLIFIER_STATUS'; result: SerializedNullifierStatusResult }
  | { ok: true; type: 'POI_REFRESHED' }
  | { ok: true; type: 'PENDING_SPENT_POIS'; railgunTxids: string[] }
  | { ok: true; type: 'SDK_RESULT'; resultEncoded: unknown }
  | { ok: true; type: 'SESSION_LIST'; sessions: SerializedChainSession[] }
  | { ok: true; type: 'SESSION_INFO'; session: SerializedChainSession }
  | { ok: true; type: 'SHARED_DB_POC_RESULT'; result: SharedDbPocResult }
  | { ok: false; type: 'ERROR'; message: string }

/** P1 POC outcome (railgun-spend-engine-warm-plan.md §6). */
export type SharedDbPocResult = {
  ok: boolean
  perChain: Array<{
    chainId: number
    workerTokens: Array<{ tokenAddress: string; amount: string }>
    mainTokens: Array<{ tokenAddress: string; amount: string }>
    match: boolean
  }>
  mismatches: string[]
  errors: string[]
}

/**
 * Engine events forwarded offscreen → SW. Payloads carry the same shape as the
 * SDK callback args, but with `chain` flattened (chain.type/id) and any nested
 * Maps converted to arrays. Receiver re-hydrates when re-emitting via railgunEmitter.
 */
export type RailgunOffscreenPushEvent =
  | {
      type: 'OFFSCREEN_EVENT'
      event:
        | 'balanceUpdate'
        | 'poiProofProgress'
        | 'utxoScanUpdate'
        | 'txidScanUpdate'
        | 'batchListUpdate'
      // payload is JSON-cloned SDK event; consumer casts.
      payload: unknown
    }
  | {
      // Progress callback from a long-running SDK call (proof generation).
      // Correlates back to the SDK_INVOKE that supplied `progressId`.
      type: 'OFFSCREEN_PROGRESS'
      progressId: string
      progress: number
    }
  | {
      type: 'OFFSCREEN_BALANCE_RESULT'
      requestId: string
      ok: true
      result: SerializedBalanceResult
    }
  | {
      type: 'OFFSCREEN_BALANCE_RESULT'
      requestId: string
      ok: false
      error: string
    }

export type RailgunOffscreenEnvelope = {
  target: typeof RAILGUN_OFFSCREEN_TARGET
  request?: RailgunOffscreenRequest
  push?: RailgunOffscreenPushEvent
}

export function makeEnvelope(request: RailgunOffscreenRequest): RailgunOffscreenEnvelope {
  return { target: RAILGUN_OFFSCREEN_TARGET, request }
}

export function makePushEnvelope(push: RailgunOffscreenPushEvent): RailgunOffscreenEnvelope {
  return { target: RAILGUN_OFFSCREEN_TARGET, push }
}

export function isRailgunEnvelope(value: unknown): value is RailgunOffscreenEnvelope {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { target?: unknown }).target === RAILGUN_OFFSCREEN_TARGET
  )
}
